import { useVuelidate } from '@vuelidate/core';
import { helpers } from '@vuelidate/validators';
import { mapGetters } from 'vuex';
import {
  validateField,
  skipValidation,
} from '../../../helpers/fieldValidation';
import { getFieldVariables } from '../../../helpers/automationUtils';

function validateActionStepFormFields(step, triggerRegistry, context) {
  const triggerAttributes = step.attributes || {};
  const formFields = triggerRegistry.form_fields || [];
  formFields.forEach(field => {
    const fieldName = field.name;
    const triggerAttributeValue = triggerAttributes[fieldName] ?? field.default;
    if (skipValidation(field, triggerAttributes, context)) return;
    validateField(field, triggerAttributeValue, context);
  });
}

function validateActionStepOutputMappingFields(step, triggerRegistry) {
  const triggerOutputMapping = step.input_mapping || {};
  const inputMappingFields = triggerRegistry.input_mapping_fields || [];
  inputMappingFields.forEach(field => {
    const fieldName = field.name;
    const triggerOutputMappingValue =
      triggerOutputMapping[fieldName] ?? field.default;
    validateField(field, triggerOutputMappingValue);
  });
}

const triggerValidation = params =>
  helpers.withParams(
    {
      value: params,
    },
    step => {
      const { triggerRegistry, context } = params;
      if (!triggerRegistry) return true;
      try {
        validateActionStepFormFields(step, triggerRegistry, context);
        validateActionStepOutputMappingFields(step, triggerRegistry);
        return true;
      } catch (error) {
        return false;
      }
    }
  );

const getVariables = (outputMapping, outputMappingFields) => {
  const variables = getFieldVariables(outputMapping, outputMappingFields);
  const variableNames = variables.map(({ name }) => name);
  return variableNames;
};

const variablesValidation = params =>
  helpers.withParams(
    {
      value: params,
    },
    step => {
      try {
        const [triggerRegistry, inheritedVariables] = params;
        const inputMappingFields = triggerRegistry?.input_mapping_fields ?? [];
        const inputMapping = step.input_mapping ?? {};
        const inputMappingVariables = getVariables(
          inputMapping,
          inputMappingFields
        );
        const invalidVariables = inputMappingVariables.filter(
          inputMappingVariable => {
            if (!inputMappingVariable) return false;
            if (!/^[a-zA-Z0-9_]+$/.test(inputMappingVariable)) return true;
            return false;
          }
        );
        if (invalidVariables.length > 0) {
          throw new Error(
            `Variables ${invalidVariables.join(
              ', '
            )} must not contain special characters.`
          );
        }
        const duplicateVariables = inputMappingVariables.filter(
          inputMappingVariable => {
            const matched = inheritedVariables.filter(inheritedVariable => {
              if (inheritedVariables.wrapper) return false;
              return inheritedVariable.name === inputMappingVariable;
            });
            return matched.length > 1;
          }
        );
        if (duplicateVariables.length > 0) {
          throw new Error(
            `Variables ${duplicateVariables.join(', ')} cannot be used anymore.`
          );
        }
        return true;
      } catch (error) {
        return false;
      }
    }
  );
export default {
  setup() {
    return { v$: useVuelidate() };
  },
  computed: {
    ...mapGetters({
      dynamicFlagsGetter: 'wgptAutomationRegistry/getDynamicFlags',
    }),
  },
  watch: {
    triggerRegistry: {
      handler() {
        this.$nextTick(() => {
          if (this.v$.trigger) {
            this.v$.trigger.$touch();
          }
        });
      },
      immediate: true,
    },
  },
  validations() {
    const context = {
      getDynamicFlags: this.dynamicFlagsGetter,
    };
    return {
      trigger: {
        triggerValidation: triggerValidation({
          triggerRegistry: this.triggerRegistry,
          context,
        }),
        variablesValidation: variablesValidation([
          this.triggerRegistry,
          this.variables,
        ]),
      },
    };
  },
};
